import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Button, Grid } from '@mui/material'
import { LanguageUtils, PlusButton } from '@pbt/pbt-ui-components'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import { DeleteIcon } from '~/components/elements/Icons/generated'
import Typography from '~/components/elements/Typography/Typography'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'

import type { ReceiptFieldGroup } from './hooks/useReceiptFields'
import { OrderItemReceipt } from './OrderItemReceipt'

interface OrderItemReceiptsListProps {
  addDraftReceipt: () => void
  deleteDraftReceipt: (id: string) => void
  draftReceiptFields: ReceiptFieldGroup[]
  inventoryOrderItem: InventoryOrderItem
  isViewOnly?: boolean
  receiptFields: ReceiptFieldGroup[]
}

export const OrderItemReceiptsList = ({
  inventoryOrderItem,
  receiptFields,
  draftReceiptFields,
  deleteDraftReceipt,
  addDraftReceipt,
  isViewOnly = false,
}: OrderItemReceiptsListProps) => {
  const { t } = useTranslation('Common')
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PackageType = useSelector(getPackageType)

  const productSizeUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )
  const packageType = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageType.id,
    PackageType,
  )
  const perPackageLabel = `${packageType} (${inventoryOrderItem.perPackageAmount})`

  const handleDeleteButtonClick = (id: string) => {
    if (isUpdating) {
      return
    }

    deleteDraftReceipt(id)
  }

  const handlePlusButtonClick = () => {
    if (isUpdating) {
      return
    }

    addDraftReceipt()
  }

  const renderReceipts = (receipts: ReceiptFieldGroup[], isDraft?: boolean) =>
    receipts.map(({ id, fields }, index) => {
      const canDelete =
        !isViewOnly && isDraft && (receiptFields.length > 0 || index > 0)
      return (
        <Grid container alignItems="center" key={id} spacing={2}>
          <Grid item xs={10}>
            <OrderItemReceipt
              fields={fields}
              isDraft={isDraft}
              isViewOnly={isViewOnly}
              orderedQuantity={inventoryOrderItem.orderedQuantity}
              perPackageAmount={inventoryOrderItem.perPackageAmount}
              perPackageLabel={perPackageLabel}
              productSizeUnit={productSizeUnit}
            />
          </Grid>
          {canDelete && (
            <Grid item xs={2}>
              <Button
                disabled={isUpdating}
                size="large"
                startIcon={<DeleteIcon />}
                sx={(theme) => ({
                  color: theme.colors.grayGray2,
                  textTransform: 'none',
                })}
                variant="text"
                onClick={() => handleDeleteButtonClick(id)}
              >
                <Typography.ParagraphLg color="grayGray2">
                  {t('Common:DELETE_ROW')}
                </Typography.ParagraphLg>
              </Button>
            </Grid>
          )}
        </Grid>
      )
    })

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {renderReceipts(receiptFields)}
      {renderReceipts(draftReceiptFields, true)}
      {!isViewOnly && (
        <PlusButton
          label={t('Common:ADD_ROW')}
          sx={{ width: 'fit-content' }}
          onClick={handlePlusButtonClick}
        />
      )}
    </Box>
  )
}
